import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";

import {ordersService} from "../../../services/orders-services";


const initialState = {
    ordersA: [],
    singleOrder: {},
    menuDishesPacksForDates: [],
    dishesOrderByDate: [],
    productsOrderByDate: [],
    deliveriesByDate: [],
    selectedDatesArray: [],
    currentDaysCount: 1,
    currentPrice: 0,
    currentDiscount: 0,
    // Promo code state
    promoCode: "",
    appliedPromoCode: null,
    appliedPromoCodeStr: "",
    promoBreakdown: null,
    promoError: "",
    promoDiscount: 0,
    isPromoCodeLoading: false,
    // Bonus points state
    bonusPointsToUse: 0,
    // Pricing state
    pricing: null,
    isPricingLoading: false,
    pricingError: null,
    filterParams: {
        word: '',
        searchField: 'orderId',
        startDate: '',
        endDate: '',
        status: []
    },
    status: null,
    error: null,
    trigger: 0,
    productsQueryParams: {
        sortBy: 'createdAt',
        sortDirection: 'desc',
        groupId: undefined,
    },
    dateRange: {
        startDate: '',
        endDate: ''
    },
};


const getAllOrders = createAsyncThunk(
    'ordersSlice/getAllOrders',
    async ({page, word, startDate, endDate, status, searchField}, {rejectWithValue}) => {
        try {
            return await ordersService.getAllOrders({page, word, startDate, endDate, status, searchField});
        } catch (e) {
            return rejectWithValue(e.response?.data?.message || "Something went wrong");
        }
    }
);


const getById = createAsyncThunk(
    'ordersSlice/getById',
    async ({id}, {rejectWithValue}) => {
        try {
            return await ordersService.getOrderById(id);
        } catch (e) {
            return rejectWithValue(e.response?.data?.message || "Something went wrong");
        }
    }
);

const getDishesOrderByDate = createAsyncThunk(
    'ordersSlice/getDishesOrderByDate',
    async ({productIds, startDate, endDate}, {rejectWithValue}) => {
        try {
            return await ordersService.getDishesByDate(productIds.join(','), startDate, endDate);
        } catch (e) {
            return rejectWithValue(e.response?.data?.message || "Something went wrong");
        }
    }
);

const getProductsOrderByDate = createAsyncThunk(
    'ordersSlice/getProductsOrderByDate',
    async ({startDate, endDate}, {getState, rejectWithValue}) => {
        try {
            const { orders } = getState();
            if (startDate && endDate) {
                return await ordersService.getProductsByDateRange(startDate, endDate, orders.productsQueryParams);
            } else {
                // Fallback to single date for backward compatibility
                return await ordersService.getProductsByDate(startDate || endDate, orders.productsQueryParams);
            }
        } catch (e) {
            return rejectWithValue(e.response?.data?.message || "Something went wrong");
        } 
    }
);

const getDeliveriesByDate = createAsyncThunk(
    'ordersSlice/getDeliveriesByDate',
    async ({date}, {rejectWithValue}) => {
        try {
            return await ordersService.getDeliveriesByDate(date);
        } catch (e) {
            return rejectWithValue(e.response?.data?.message || "Something went wrong");
        }
    }
);

const getDeliveriesByDateRange = createAsyncThunk(
    'ordersSlice/getDeliveriesByDateRange',
    async ({startDate, endDate}, {rejectWithValue}) => {
        try {
            return await ordersService.getDeliveriesByDateRange(startDate, endDate);
        } catch (e) {
            return rejectWithValue(e.response?.data?.message || "Something went wrong");
        }
    }
);


const ordersSlice = createSlice({
    name: "ordersSlice",
    initialState,
    reducers: {
        changeTrigger: (state) => {
            state.trigger += 1;
        },
        setProductsQueryParams: (state, action) => {
            state.productsQueryParams = {
                ...state.productsQueryParams,
                ...(action.payload || {})
            };
        },
        setDateRange: (state, action) => {
            state.dateRange = {
                ...state.dateRange,
                ...action.payload
            };
        },
        setFilterParams: (state, action) => {
            state.filterParams = {
                ...state.filterParams,
                ...action.payload
            };
        },
        clearFilters: (state) => {
            state.filterParams = {
                word: '',
                searchField: 'orderId',
                startDate: '',
                endDate: '',
                status: []
            };
        },
        changeSelectedDatesArray: (state, action) => {
            state.selectedDatesArray = action.payload;
        },
        changeMenuDishesPacksForDates: (state, action) => {
            state.menuDishesPacksForDates = action.payload;
        },
        changeCurrentDaysCount: (state, action) => {
            state.currentDaysCount = action.payload;
        },
        changeCurrentPrice: (state, action) => {
            state.currentPrice = action.payload;
        },
        changeCurrentDiscount: (state, action) => {
            state.currentDiscount = action.payload;
        },
        // Promo code reducers
        setPromoCode: (state, action) => {
            state.promoCode = action.payload;
        },
        setAppliedPromoCode: (state, action) => {
            state.appliedPromoCode = action.payload;
        },
        setAppliedPromoCodeStr: (state, action) => {
            state.appliedPromoCodeStr = action.payload;
        },
        setPromoBreakdown: (state, action) => {
            state.promoBreakdown = action.payload;
        },
        setPromoError: (state, action) => {
            state.promoError = action.payload;
        },
        setPromoDiscount: (state, action) => {
            state.promoDiscount = action.payload;
        },
        setPromoCodeLoading: (state, action) => {
            state.isPromoCodeLoading = action.payload;
        },
        resetPromoCode: (state) => {
            state.promoCode = "";
            state.appliedPromoCode = null;
            state.appliedPromoCodeStr = "";
            state.promoBreakdown = null;
            state.promoError = "";
            state.promoDiscount = 0;
            state.isPromoCodeLoading = false;
        },
        // Bonus points reducers
        setBonusPointsToUse: (state, action) => {
            state.bonusPointsToUse = action.payload;
        },
        resetBonusPoints: (state) => {
            state.bonusPointsToUse = 0;
        },
        // Pricing reducers
        setPricing: (state, action) => {
            state.pricing = action.payload;
        },
        setPricingLoading: (state, action) => {
            state.isPricingLoading = action.payload;
        },
        setPricingError: (state, action) => {
            state.pricingError = action.payload;
        },
        resetPricing: (state) => {
            state.pricing = null;
            state.isPricingLoading = false;
            state.pricingError = null;
        }
    },
    extraReducers: builder =>
        builder
            .addCase(getAllOrders.pending, (state) => {
                state.status = "Loading";
                state.error = null;
            })
            .addCase(getAllOrders.fulfilled, (state, action) => {
                state.status = "fulfilled";
                state.ordersA = action.payload;
            })
            .addCase(getAllOrders.rejected, (state, action) => {
                state.status = "rejected";
                state.error = action.payload;
            })

            .addCase(getById.pending, (state) => {
                state.status = "Loading";
                state.error = null;
            })
            .addCase(getById.fulfilled, (state, action) => {
                state.status = "fulfilled";
                state.singleOrder = action.payload;
            })
            .addCase(getById.rejected, (state, action) => {
                state.status = "rejected";
                state.error = action.payload;
            })

            .addCase(getDishesOrderByDate.pending, (state) => {
                state.status = "Loading";
                state.error = null;
            })
            .addCase(getDishesOrderByDate.fulfilled, (state, action) => {
                state.status = "fulfilled";
                state.dishesOrderByDate = action.payload;
            })
            .addCase(getDishesOrderByDate.rejected, (state, action) => {
                state.status = "rejected";
                state.error = action.payload;
            })

            .addCase(getProductsOrderByDate.pending, (state) => {
                state.status = "Loading";
                state.error = null;
            })
            .addCase(getProductsOrderByDate.fulfilled, (state, action) => {
                state.status = "fulfilled";
                state.productsOrderByDate = action.payload;
            })
            .addCase(getProductsOrderByDate.rejected, (state, action) => {
                state.status = "rejected";
                state.error = action.payload;
            })

            .addCase(getDeliveriesByDateRange.pending, (state) => {
                state.status = "Loading";
                state.error = null;
            })
            .addCase(getDeliveriesByDateRange.fulfilled, (state, action) => {
                state.status = "fulfilled";
                state.deliveriesByDate = action.payload;
            })
            .addCase(getDeliveriesByDateRange.rejected, (state, action) => {
                state.status = "rejected";
                state.error = action.payload;
            })

            .addCase(getDeliveriesByDate.pending, (state) => {
                state.status = "Loading";
                state.error = null;
            })
            .addCase(getDeliveriesByDate.fulfilled, (state, action) => {
                state.status = "fulfilled";
                state.deliveriesByDate = action.payload;
            })
            .addCase(getDeliveriesByDate.rejected, (state, action) => {
                state.status = "rejected";
                state.error = action.payload;
            })
});

const {reducer: ordersReducer, actions} = ordersSlice;

const ordersActions = {
    ...actions,
    getAllOrders,
    getById,
    getDishesOrderByDate,
    getProductsOrderByDate,
    getDeliveriesByDateRange,
    getDeliveriesByDate
};

export {
    ordersReducer,
    ordersActions
};
